import { useRef } from 'react';
import type { ReactNode } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { WS_COLOR } from './tokens';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Say what will happen and, for a destructive action, what cannot be undone. */
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** `destructive` fills the confirm button in the error ink (9.11:1 with white). */
  tone?: 'default' | 'destructive';
  busy?: boolean;
  /** Holds the confirm button back, for a typed confirmation that has not been typed yet. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Asks before something is hard to reverse. One filled button (the confirm), one tonal
 * (cancel); focus starts on Cancel for a destructive action so Enter cannot delete by habit.
 * When the content holds a field, focus starts in the field instead.
 *
 * Focus is set once the dialog has finished appearing, not at mount: a browser will not give
 * focus to an element that is still fading in, and the dialog would be left with focus on its
 * invisible container, which a screen reader announces as nothing at all.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'default',
  busy = false,
  confirmDisabled = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const destructive = tone === 'destructive';
  const initial = useRef<HTMLButtonElement>(null);
  const focusStart = (container: HTMLElement) => {
    const paper = container.querySelector<HTMLElement>('[role="dialog"]');
    const active = document.activeElement;
    if (paper && active && active !== paper && paper.contains(active)) return; // already inside
    const field = paper?.querySelector<HTMLElement>('input, textarea, select');
    (field ?? initial.current)?.focus();
  };
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      TransitionProps={{ onEntered: (node) => focusStart(node as HTMLElement) }}
      maxWidth="xs"
      fullWidth
      aria-labelledby="confirm-title"
      aria-describedby="confirm-body"
    >
      <DialogTitle id="confirm-title" sx={{ fontSize: 18, lineHeight: '24px', fontWeight: 600 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography id="confirm-body" variant="body2" component="div">
          {children}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 6, pb: 5, gap: 2 }}>
        <Button
          variant="tonal"
          onClick={onClose}
          disabled={busy}
          ref={destructive ? initial : undefined}
        >
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={busy || confirmDisabled}
          ref={destructive ? undefined : initial}
          sx={
            destructive
              ? {
                  backgroundColor: WS_COLOR.destructive,
                  '&:hover': { backgroundColor: WS_COLOR.destructive },
                }
              : undefined
          }
        >
          {busy ? 'Working…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
